/**
 * Dashboard Page Component
 *
 * Main landing page showing advisor metrics, quick actions, and recent activity.
 * Uses DashboardGrid and MetricCard from the institutional UI Kit.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { advisorClient } from '../api/client';
import { AdvisorServiceMissingError, AdvisorServiceNotice, advisorFailure } from '../components/AdvisorServiceNotice';
import type { components } from '@aaimplatform/institutional-types/advisor-tools';
import {
  MetricCard,
  DashboardGrid,
  DashboardWidget,
  Heading,
  BodyText,
  Card,
  Button,
} from '@aaimplatform/ui';

type AdvisorMetrics = components['schemas']['AdvisorMetrics'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const navigate = useNavigate();

  const { data: metrics, isLoading, error } = useQuery<AdvisorMetrics>({
    queryKey: ['dashboard-metrics'],
    queryFn: async () => {
      const { data, error, response } = await advisorClient.GET('/api/advisors/metrics');
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load dashboard metrics');
      return data;
    },
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            Here is what is happening with your clients today
          </BodyText>
        </div>
      </div>

      {error instanceof AdvisorServiceMissingError && <AdvisorServiceNotice className="mb-6" />}

      {/* Portfolio Metrics */}
      <DashboardGrid columns={4} gap="compact" className="mb-6" loading={isLoading}>
        <DashboardWidget id="total-clients">
          <MetricCard
            label="Total Clients"
            value={String(metrics?.totalClients ?? 0)}
            valueColor="primary"
          />
        </DashboardWidget>
        <DashboardWidget id="active-loans">
          <MetricCard
            label="Active Loans"
            value={String(metrics?.activeLoans ?? 0)}
            valueColor="accent"
          />
        </DashboardWidget>
        <DashboardWidget id="loan-volume">
          <MetricCard
            label="Total Loan Volume"
            value={formatCurrency(metrics?.totalLoanVolume ?? 0)}
            valueColor="primary"
          />
        </DashboardWidget>
        <DashboardWidget id="avg-loan">
          <MetricCard
            label="Average Loan Size"
            value={formatCurrency(metrics?.averageLoanSize ?? 0)}
            valueColor="primary"
          />
        </DashboardWidget>
      </DashboardGrid>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <Heading level={3} as={3} size="card" className="mb-4">Quick Actions</Heading>
          <div className="flex flex-col gap-3">
            <Button variant="primary" className="w-full" onClick={() => navigate('/calculator')}>
              Calculate Pledging Capacity
            </Button>
            <Button variant="secondary" className="w-full" onClick={() => navigate('/proposals/new')}>
              Create New Proposal
            </Button>
            <Button variant="secondary" className="w-full" onClick={() => navigate('/clients')}>
              View All Clients
            </Button>
            <Button variant="secondary" className="w-full" onClick={() => navigate('/lending/workflows')}>
              Lending Workflows
            </Button>
            <Button variant="secondary" className="w-full" onClick={() => navigate('/collateral')}>
              Collateral Monitoring
            </Button>
          </div>
        </Card>

        <Card>
          <Heading level={3} as={3} size="card" className="mb-4">Recent Activity</Heading>
          <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto">
            {metrics?.recentActivity && metrics.recentActivity.length > 0 ? (
              metrics.recentActivity.map((activity) => (
                <div key={activity.id} className="p-3 bg-recessed rounded border border-surface">
                  <BodyText className="font-semibold text-accent capitalize mb-1">
                    {activity.type.replace(/_/g, ' ')}
                  </BodyText>
                  <BodyText className="mb-1">{activity.description}</BodyText>
                  <BodyText className="text-tertiary text-xs">{formatDate(activity.timestamp)}</BodyText>
                </div>
              ))
            ) : (
              <BodyText className="text-secondary">No recent activity</BodyText>
            )}
          </div>
        </Card>
      </div>

      {metrics?.pendingProposals && metrics.pendingProposals > 0 && (
        <Card className="bg-accent/5 border border-accent">
          <Heading level={3} as={3} size="label" className="text-accent mb-2">Pending Proposals</Heading>
          <BodyText className="mb-4">
            You have {metrics.pendingProposals} proposal{metrics.pendingProposals !== 1 ? 's' : ''}{' '}
            awaiting client response.
          </BodyText>
          <Button variant="secondary" onClick={() => navigate('/proposals')}>
            View Proposals
          </Button>
        </Card>
      )}
    </div>
  );
}
